import {
    Alert,
    Autocomplete,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid2,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import CommonButton from '../components/atom/CommonButton';
import { useTranslation } from 'react-i18next';
import { getVenue } from '@openapi/venues/venues';
import useNotify from '../hooks/useNotify';
import { CUSTOM_FIELDS_TYPES } from '../helpers/constants';
import { useCallback } from 'react';

function TranslationsModal({
    showModal,
    MODAL_TYPE,
    translations,
    setTranslations,
    item,
    column,
    intermediateColumn = null, // optional
    venueId,
    originalString,
    type, // optional for custom fields (can be single, multi or dropdown, default is single)
    originalData, // optional for multiline custom fields
}) {
    const { t } = useTranslation();
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [localTranslations, setLocalTranslations] = useState(translations || {});
    const { notifyError } = useNotify();

    // default to "columns.<column>" if intermediateColumn is not provided
    const itemLabel = intermediateColumn
        ? t(`${item}.${intermediateColumn}.${column}`)
        : t(`${item}.columns.${column}`);
    // if the type is dropdown, we need to join the original string with new line, because there is an array of strings
    // if the type is multi, we need to split the original string by new line,
    // otherwise we can use the original string directly
    const originalStringValue = Array.isArray(originalString)
        ? originalString.join('\n')
        : String(originalString).replace(/\\n/g, '\n');

    const getAvailableLanguages = useCallback(async () => {
        try {
            const result = await getVenue(Number(venueId));
            setAvailableLanguages(result?.availableLanguages || []);
        } catch (error) {
            notifyError(error);
        }
    }, [venueId, notifyError]);

    useEffect(() => {
        getAvailableLanguages();
    }, [venueId, getAvailableLanguages]);

    return (
        <Dialog
            aria-labelledby="translations-modal"
            open
            maxWidth="md"
            fullWidth
            onClose={() => showModal(MODAL_TYPE.NONE)}
        >
            <DialogTitle>
                {itemLabel} ({t('common.translations')})
            </DialogTitle>
            <DialogContent>
                <Grid2 container>
                    <DialogContentText id="alert-dialog-info" component="div">
                        <Alert sx={{ mt: 0.5, mb: 2 }} severity="info">
                            {t('common.languageInfo')}
                        </Alert>
                    </DialogContentText>
                    <Grid2 size={12}>
                        <TextField
                            fullWidth
                            label={`${itemLabel} (${t('common.original')})`}
                            value={originalStringValue}
                            disabled
                            multiline
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid2>
                    {availableLanguages.map((language) => (
                        <Grid2
                            key={language}
                            container
                            spacing={2}
                            size={{ xs: 12 }}
                            alignItems="center"
                        >
                            {type === CUSTOM_FIELDS_TYPES.DROPDOWN ? (
                                <>
                                    <Grid2 size={{ xs: 4 }}>
                                        <Autocomplete
                                            options={language}
                                            value={language}
                                            onChange={(_, newValue) => {
                                                setLocalTranslations({
                                                    ...localTranslations,
                                                    [language]: newValue,
                                                });
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t('common.language')}
                                                    fullWidth
                                                />
                                            )}
                                            getOptionLabel={(option) => {
                                                return `${option}`;
                                            }}
                                            disabled
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 8 }}>
                                        <TableContainer component={Paper} sx={{ mt: 2 }}>
                                            <Table>
                                                <TableBody>
                                                    {(originalData || []).map((original, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                <TextField
                                                                    fullWidth
                                                                    value={original}
                                                                    disabled
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    fullWidth
                                                                    value={
                                                                        localTranslations[
                                                                            language
                                                                        ]?.[index] || ''
                                                                    }
                                                                    onChange={(ev) => {
                                                                        const updatedTranslations =
                                                                            [
                                                                                ...(localTranslations[
                                                                                    language
                                                                                ] || []),
                                                                            ];
                                                                        updatedTranslations[index] =
                                                                            ev.target.value;
                                                                        setLocalTranslations({
                                                                            ...localTranslations,
                                                                            [language]:
                                                                                updatedTranslations,
                                                                        });
                                                                    }}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid2>
                                </>
                            ) : (
                                <>
                                    <Grid2 size={{ xs: 4 }}>
                                        <Autocomplete
                                            options={language}
                                            value={language}
                                            onChange={(_, newValue) => {
                                                setLocalTranslations({
                                                    ...localTranslations,
                                                    [language]: newValue,
                                                });
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t('common.language')}
                                                    fullWidth
                                                />
                                            )}
                                            getOptionLabel={(option) => {
                                                return `${option}`;
                                            }}
                                            disabled
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 8 }}>
                                        <TextField
                                            // default case for single and multi text fields
                                            fullWidth
                                            label={`${itemLabel} (${language})`}
                                            value={localTranslations[language] || ''}
                                            multiline={type === CUSTOM_FIELDS_TYPES.MULTI}
                                            onChange={(ev) =>
                                                setLocalTranslations({
                                                    ...localTranslations,
                                                    [language]: ev.target.value,
                                                })
                                            }
                                        />
                                    </Grid2>
                                </>
                            )}
                        </Grid2>
                    ))}
                </Grid2>
            </DialogContent>
            <DialogActions>
                <CommonButton
                    variant="inherit"
                    onClick={() => showModal(MODAL_TYPE.NONE)}
                    label={t('common.cancel')}
                    buttonType="secondary"
                />
                <CommonButton
                    onClick={() => {
                        setTranslations(localTranslations);
                        showModal(MODAL_TYPE.NONE);
                    }}
                    label={t('common.set')}
                    buttonType="primary"
                />
            </DialogActions>
        </Dialog>
    );
}

export default TranslationsModal;
